import { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useParams, useNavigate } from 'react-router-dom';
import { useMusicians } from '../hooks/useMusicians';
import { useProfileContext } from '../hooks/useProfileContext';
import { useConfig } from '../hooks/useConfig';
import { useRackUsage } from '../hooks/useRackUsage';
import { useEntityEditor, entityNameUnique } from '../hooks/useEntityEditor';
import { bandUrlBase, nameToSlug } from '../utils/slug';
import InstrumentEditor from '../components/InstrumentEditor';
import MonitoringSection from '../components/MonitoringSection';
import AutoTextarea from '../components/AutoTextarea';
import CollapsibleSection from '../components/CollapsibleSection';
import NumberPicker from '../components/NumberPicker';
import BacklineEditor from '../components/BacklineEditor';
import Button from '../components/ui/Button';
import PageHeader from '../components/PageHeader';
import { detectInstrumentConflict, formatConflictMessage } from '../lib/instrumentConflict';
import type { Musician, Instrument, DietType, TransportType } from '../types';
import { createInstrument, createBacklineItem, mergeStageRacks } from '../types';

// Extra roles a musician can hold for a band (the person is already a musician
// by category). Mirrors crew tags; 'musician' is omitted on purpose.
const MUSICIAN_TAG_OPTIONS = [
  'manager', 'tour manager', 'foh', 'mon', 'lights', 'driver', 'stagehand', 'video', 'guest', 'other',
  'guitar tech', 'drum tech', 'keys tech',
];

export default function MusicianEditPage() {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const isNew = id === 'new' || location.pathname.endsWith('/musicians/new');
  const { activeProfileId, getProfile } = useProfileContext();
  const activeProfile = activeProfileId ? getProfile(activeProfileId) : null;
  const bandSlug = activeProfile?.slug || activeProfileId || '';
  const base = activeProfileId ? bandUrlBase(bandSlug, activeProfile?.name) : '';
  const {
    musicians,
    addMusician,
    updateMusician,
    saveMusiciansToServer,
    getMusician,
  } = useMusicians();
  const { config } = useConfig();
  const racks = useMemo(
    () => mergeStageRacks(config.globalRacks, config.specs, (config.rider?.transport as TransportType) || 'van'),
    [config.globalRacks, config.specs, config.rider?.transport],
  );
  const { usage } = useRackUsage({ musicians });

  // PATCH MARK favourites (2026-10-23, user): every colour used on ANY track of
  // ANY card of the band (persisted on the tracks — no extra storage). Shown as
  // clickable swatches next to each track's colour picker so a whole group of
  // tracks shares one colour ("everything to the house stagebox" = one colour).
  const markFavourites = useMemo(() => {
    const set = new Set<string>();
    for (const m of musicians) {
      for (const inst of m.instruments || []) {
        for (const tr of inst.tracks || []) {
          const c = (tr as any).patchMark;
          if (typeof c === 'string' && c) set.add(c);
        }
      }
    }
    // Monitor-return marks too (IEM / wedges / sub) — same favourites pool.
    for (const m of musicians) {
      for (const k of ['iemPatchMark', 'wedgePatchMark', 'subPatchMark'] as const) {
        const c = (m as any)[k];
        if (typeof c === 'string' && c) set.add(c);
      }
    }
    return [...set];
  }, [musicians]);

  // RENAME a favourite (2026-10-23, user): change the colour EVERYWHERE it is
  // used — every track / monitor return of the band carrying `from` becomes
  // `to` (propagated to the local list + autosaved by the hook).
  const renameFavourite = useCallback((from: string, to: string) => {
    const hit = (c: any) => typeof c === 'string' && c.toLowerCase() === from.toLowerCase();
    for (const m of musicians) {
      let changed = false;
      const instruments = (m.instruments || []).map((inst: any) => ({
        ...inst,
        tracks: (inst.tracks || []).map((tr: any) => {
          if (!hit(tr.patchMark)) return tr;
          changed = true;
          return { ...tr, patchMark: to };
        }),
      }));
      const patch: any = { instruments };
      for (const k of ['iemPatchMark', 'wedgePatchMark', 'subPatchMark'] as const) {
        if (hit((m as any)[k])) { patch[k] = to; changed = true; }
      }
      if (changed) updateMusician(m.id, patch as Partial<Musician>);
    }
  }, [musicians, updateMusician]);


  const [musician, setMusicianRaw] = useState<Musician | null>(null);

  // Shared-INHERITANCE (2026-10-23, user): for the instrument being edited, does
  // ANOTHER person's same-named instrument carry a ticked sharedWithReplacement
  // track? The replacement's checkbox then shows the inherited state (checked +
  // disabled + tooltip naming the main player) instead of a confusing free tick.
  const inheritedSharedFor = useCallback((inst: { name?: string }) => {
    const name = (inst.name || '').trim().toLowerCase();
    if (!name) return null;
    for (const m of musicians) {
      if (musician && m.id === musician.id) continue;
      for (const other of m.instruments || []) {
        if ((other.name || '').trim().toLowerCase() !== name) continue;
        for (const tr of other.tracks || []) {
          if (tr.sharedWithReplacement) return { ticked: true, by: m.name || 'Unnamed' };
        }
      }
    }
    return null;
  }, [musicians, musician]);

  // Wrapper: any edit to the local `musician` also propagates to the `musicians`
  // array so the hook's debounced auto-save persists it.
  const setMusician = useCallback((updater: Musician | null | ((prev: Musician | null) => Musician | null)) => {
    setMusicianRaw(prev => {
      const next =
        typeof updater === 'function' ? (updater as (p: Musician | null) => Musician | null)(prev) : updater;
      if (next && !isNew) updateMusician(next.id, next);
      return next;
    });
  }, [isNew, updateMusician]);

  // Shared editor behaviour (diet write-through to Team, cross-referenced
  // person views, Save guard/saving flag) — the identical half of the two
  // rider-card editors. The card state + handlers stay page-owned.
  const {
    saving,
    dietValue,
    dietNotesValue,
    nonFoodAllergiesValue,
    dietTypes,
    updateDiet,
    personInstruments,
    personTags,
    handleSave,
  } = useEntityEditor({
    scope: 'musicians',
    entity: musician,
    setEntity: setMusician,
    validate: (m, tr) => {
      if (!(m as Musician).name.trim()) return tr('musicianEdit.enterName');
      if (!entityNameUnique((m as Musician).name, musicians, (m as Musician).id)) return tr('musicianEdit.nameExists');
      return null;
    },
    failedSave: t('musicianEdit.failedSave'),
    onSave: async () => {
      const m = musician;
      if (!m) return;
      if (isNew) {
        const { updated } = addMusician({
          name: m.name,
          fonction: m.fonction || 'musician',
          iemType: m.iemType,
          wedges: m.wedges,
          subwoofer: m.subwoofer,
          color: m.color,
          instruments: m.instruments,
          active: true,
        });
        await saveMusiciansToServer(updated);
      } else {
        updateMusician(m.id, m);
        await saveMusiciansToServer(musicians.map(x => x.id === m.id ? { ...x, ...m } : x));
      }
      navigate(`${base}/musicians`);
    },
    t,
  });

  useEffect(() => {
    const routeIsNew = isNew || location.pathname.endsWith('/musicians/new');
    if (routeIsNew) {
      setMusician({
        id: '',
        name: '',
        fonction: '',
        iemType: 'stereo' as const,
        wedges: 0,
        subwoofer: false,
        color: '#e94560',
        instruments: [],
        backline: [],
        diet: 'none',
        dietNotes: '',
        towels: 1,
        dressingRoomTowels: 1,
        water: 0,
        active: true,
        order: musicians.length,
      });
    } else if (id) {
      // Try to find by ID first, then by slug (name)
      let existing = getMusician(id);
      if (!existing) {
        existing = musicians.find(m => nameToSlug(m.name) === id) ?? null;
      }
      if (existing) {
        setMusicianRaw(existing);
      }
      // Don't navigate away — just wait
    }
  }, [id, isNew, location.pathname, getMusician, musicians]);

  const handleMusicianChange = useCallback(
    (field: keyof Musician, value: any) => {
      if (musician) {
        setMusician({ ...musician, [field]: value });
      }
    },
    [musician]
  );

  // Extra roles for this band (e.g. 'tour manager' when the bassist also
  // manages the tour). Same multi-tag semantics as crew tags (2026-08-07).
  const handleToggleTag = useCallback((tag: string) => {
    if (!musician) return;
    const cur = musician.tags || [];
    const next = cur.includes(tag) ? cur.filter(t => t !== tag) : [...cur, tag];
    handleMusicianChange('tags', next);
  }, [musician, handleMusicianChange]);

  const handleInstrumentChange = useCallback(
    (updatedInst: Instrument) => {
      if (!musician) return;
      setMusician({
        ...musician,
        instruments: musician.instruments.map(inst =>
          inst.id === updatedInst.id ? { ...inst, ...updatedInst } : inst
        ),
      });
    },
    [musician]
  );

  const addInstrument = useCallback(() => {
    if (!musician) return;
    const newInst = createInstrument();
    setMusician({
      ...musician,
      instruments: [...musician.instruments, newInst],
    });
  }, [musician]);

  const moveInstrumentUp = useCallback((instrumentId: string) => {
    if (!musician) return;
    const idx = musician.instruments.findIndex(inst => inst.id === instrumentId);
    if (idx > 0) {
      const next = [...musician.instruments];
      [next[idx - 1], next[idx]] = [next[idx], next[idx - 1]];
      setMusician({ ...musician, instruments: next });
    }
  }, [musician]);

  const moveInstrumentDown = useCallback((instrumentId: string) => {
    if (!musician) return;
    const idx = musician.instruments.findIndex(inst => inst.id === instrumentId);
    if (idx < musician.instruments.length - 1) {
      const next = [...musician.instruments];
      [next[idx], next[idx + 1]] = [next[idx + 1], next[idx]];
      setMusician({ ...musician, instruments: next });
    }
  }, [musician]);

  // --- Backline handlers ---
  const addBacklineItem = useCallback(() => {
    if (!musician) return;
    setMusician({
      ...musician,
      backline: [...(musician.backline || []), createBacklineItem()],
    });
  }, [musician]);

  if (!musician) {
    return (
      <div className="page" style={{ padding: '48px', textAlign: 'center' }}>
        <p style={{ color: 'var(--text-dim)' }}>{t('musicianEdit.loading')}</p>
        {!isNew && (
          <button className="btn btn-secondary" onClick={() => navigate(`${base}/musicians`)}>
            {t('musicianEdit.backToMusicians')}
          </button>
        )}
      </div>
    );
  }

    return (
      <div className="page musician-edit-page" data-edit-page="musician" data-density="socle">
      <PageHeader
        title={isNew ? t('musicianEdit.newMusician') : t('musicianEdit.editTitle', { name: musician.name || t('musicianEdit.musician') })}
        actions={
          <>
            <Button variant="secondary" data-edit-back onClick={() => navigate(`${base}/musicians`)}>
              {t('musicianEdit.backToList')}
            </Button>
            {isNew && (
              <Button variant="primary" data-edit-save onClick={handleSave} disabled={saving}>
                {saving ? t('musicianEdit.saving') : t('musicianEdit.createMusician')}
              </Button>
            )}
          </>
        }
      />

      <div className="musician-form">
        <CollapsibleSection title={t('musicianEdit.musicianInfo')}>
          <div className="form-grid">
            <div className="form-group">
              <label>{t('musicianEdit.name')}</label>
              <input
                type="text"
                data-edit-field="name"
                value={musician.name}
                onChange={e => handleMusicianChange('name', e.target.value)}
                placeholder={t('musicianEdit.riderName')}
                required
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label>{t('musicianEdit.fonction')}</label>
              <input
                type="text"
                value={musician.fonction}
                onChange={e => handleMusicianChange('fonction', e.target.value)}
                placeholder={t('musicianEdit.fonctionPh')}
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label>{t('musicianEdit.baseRole')}</label>
              <select
                value={musician.baseRole || 'main'}
                onChange={e => handleMusicianChange('baseRole', e.target.value as 'main' | 'session' | 'guest')}
                className="form-input"
              >
                <option value="main">{t('musicianEdit.mainPermanent')}</option>
                <option value="session">{t('musicianEdit.sessionDeputy')}</option>
                <option value="guest">{t('musicianEdit.guest')}</option>
              </select>
              <small style={{ color: 'var(--text-dim)' }}>{t('musicianEdit.baseRoleHint')}</small>
            </div>

            <div className="form-group">
              <label>{t('musicianEdit.color')}</label>
              <input
                type="color"
                className="form-input"
                value={musician.color}
                onChange={e => handleMusicianChange('color', e.target.value)}
              />
            </div>

            <div className="form-group" style={{ gridColumn: '1 / -1' }}>
              <label>{t('musicianEdit.extraRoles')}</label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {MUSICIAN_TAG_OPTIONS.map((tag) => {
                  const active = (musician.tags || []).includes(tag);
                  return (
                    <button
                      key={tag}
                      type="button"
                      className={`rc-pill${active ? ' on' : ''}`}
                      onClick={() => handleToggleTag(tag)}
                    >
                      {tag}
                    </button>
                  );
                })}
              </div>
              <small style={{ color: 'var(--text-dim)' }}>
                {t('musicianEdit.extraRolesHint')}
              </small>
            </div>
          </div>
        </CollapsibleSection>

        {/* Hospitality per-person (towels & water are person-linked so the show
            rider totals follow the SELECTED people — 2026-08-07).
            Placed right after Musician Info, like Crew (2026-08-09). */}
        <CollapsibleSection title={t('musicianEdit.hospitality')}>
          {/* Diet first — the dietary requirement is the headline for this person. */}
          <div className="form-grid" style={{ marginBottom: 14 }}>
            <div className="form-group">
              <label>{t('musicianEdit.diet')}</label>
              <input
                className="form-input"
                list="diet-types-suggestions"
                value={dietValue}
                onChange={(e) => updateDiet({ diet: e.target.value as DietType })}
                placeholder={t('musicianEdit.dietPh')}
              />
              <small style={{ color: 'var(--text-dim)' }}>{t('musicianEdit.dietStoredTeam')}</small>
            </div>
            <div className="form-group">
              <label>{t('musicianEdit.dietDetails')}</label>
              <AutoTextarea
                value={dietNotesValue}
                onChange={(e) => updateDiet({ dietNotes: e.target.value })}
                placeholder={t('musicianEdit.dietDetailsPh')}
                className="form-input"
              />
            </div>
            <div className="form-group">
              <label>{t('musicianEdit.nonFoodAllergies')}</label>
              <AutoTextarea
                value={nonFoodAllergiesValue}
                onChange={(e) => updateDiet({ nonFoodAllergies: e.target.value })}
                placeholder={t('musicianEdit.nonFoodAllergiesPh')}
                className="form-input"
              />
              <small style={{ color: 'var(--text-dim)' }}>{t('musicianEdit.dietStoredTeam')}</small>
            </div>
          </div>
          {/* Towels & water on the row below — these are person-linked so the show
              rider totals follow the SELECTED people (2026-08-07). */}
          <div className="form-grid">
            <div className="form-group">
              <label>{t('musicianEdit.stageTowels')}</label>
              <NumberPicker
                value={musician.towels ?? 1}
                options={[0, 1, 2, 3, 4, 5, 6, 8]}
                onChange={(v) => handleMusicianChange('towels', v)}
                className="form-input"
              />
              <small style={{ color: 'var(--text-dim)' }}>{t('musicianEdit.blackTowels')}</small>
            </div>
            <div className="form-group">
              <label>{t('musicianEdit.dressingTowels')}</label>
              <NumberPicker
                value={musician.dressingRoomTowels ?? 1}
                options={[0, 1, 2, 3, 4, 5, 6, 8]}
                onChange={(v) => handleMusicianChange('dressingRoomTowels', v)}
                className="form-input"
              />
              <small style={{ color: 'var(--text-dim)' }}>{t('musicianEdit.dressingTowelsHint')}</small>
            </div>
            <div className="form-group">
              <label>{t('musicianEdit.waterBottles')}</label>
              <NumberPicker
                value={musician.water ?? 0}
                options={[0, 1, 2, 3, 4, 5, 6, 8, 10, 12]}
                onChange={(v) => handleMusicianChange('water', v)}
                className="form-input"
              />
              <small style={{ color: 'var(--text-dim)' }}>{t('musicianEdit.waterHint')}</small>
            </div>
          </div>
        </CollapsibleSection>

        <CollapsibleSection title={t('musicianEdit.monitoring')}>
          <MonitoringSection
            iemType={musician.iemType}
            iemWired={musician.iemWired}
            iemWiredProvidedBy={musician.iemWiredProvidedBy}
            wedges={musician.wedges}
            subwoofer={musician.subwoofer}
            iemRackId={musician.iemRackId}
            iemOutStart={musician.iemOutStart}
            wedgeRackId={musician.wedgeRackId}
            wedgeOutStart={musician.wedgeOutStart}
            wedgesStereo={musician.wedgesStereo}
            subRackId={musician.subRackId}
            subOutStart={musician.subOutStart}
            iemPatchMark={musician.iemPatchMark}
            wedgePatchMark={musician.wedgePatchMark}
            subPatchMark={musician.subPatchMark}
            personMarks={markFavourites}
            onRenameFavourite={renameFavourite}
            racks={racks}
                        usage={usage}
                        editedInstruments={new Set(musician.instruments.map((i) => i.name).filter(Boolean))}
                        personInstruments={personInstruments}
                        editedTags={new Set(musician.tags || [])}
                        personTags={personTags}
                        personLabel={musician.name || 'Unnamed'}
                                                onChange={(patch) => {
                                      if (patch.iemType !== undefined) handleMusicianChange('iemType', patch.iemType);
              if (patch.iemWired !== undefined) handleMusicianChange('iemWired', patch.iemWired);
              if (patch.wedges !== undefined) handleMusicianChange('wedges', patch.wedges);
              if (patch.subwoofer !== undefined) handleMusicianChange('subwoofer', patch.subwoofer);
              if (patch.iemRackId !== undefined) handleMusicianChange('iemRackId', patch.iemRackId);
              if (patch.iemOutStart !== undefined) handleMusicianChange('iemOutStart', patch.iemOutStart);
              if (patch.wedgeRackId !== undefined) handleMusicianChange('wedgeRackId', patch.wedgeRackId);
              if (patch.wedgeOutStart !== undefined) handleMusicianChange('wedgeOutStart', patch.wedgeOutStart);
              if (patch.wedgesStereo !== undefined) handleMusicianChange('wedgesStereo', patch.wedgesStereo);
              if (patch.subRackId !== undefined) handleMusicianChange('subRackId', patch.subRackId);
              if (patch.subOutStart !== undefined) handleMusicianChange('subOutStart', patch.subOutStart);
              // PATCH MARKS: undefined MEANS "clear" — 'patchMark' in patch
              // (presence), not a !== undefined filter (v0.1.67 fix, user:
              // « remove the color ne fonctionne pas sur les outputs »).
              if ('iemPatchMark' in patch) handleMusicianChange('iemPatchMark', patch.iemPatchMark);
              if ('wedgePatchMark' in patch) handleMusicianChange('wedgePatchMark', patch.wedgePatchMark);
              if ('subPatchMark' in patch) handleMusicianChange('subPatchMark', patch.subPatchMark);
            }}
          />
        </CollapsibleSection>

        <CollapsibleSection
          title={t('musicianEdit.instruments')}
          count={musician.instruments.length}
          headerActions={
            <Button variant="primary" size="sm" onClick={addInstrument}>
              {t('musicianEdit.addInstrument')}
            </Button>
          }
        >

          {musician.instruments.length === 0 && (
            <div className="empty-state">
              <p>{t('musicianEdit.noInstruments')}</p>
            </div>
          )}

          <div className="instrument-list">
            {musician.instruments.map((inst) => {
              const allPersonTracks = musician.instruments.flatMap((i) => i.tracks);
              const conflict = detectInstrumentConflict({
                tracks: inst.tracks,
                allPersonTracks,
                racks,
                usage,
                personLabel: musician.name || 'Unnamed',
                instrumentName: inst.name,
                personInstruments,
              });
              return (
              <InstrumentEditor
                key={inst.id}
                instrument={inst}
                racks={racks}
                                usage={usage}
                                editedInstruments={new Set(musician.instruments.map((i) => i.name).filter(Boolean))}
                                personInstruments={personInstruments}
                                editedTags={new Set(musician.tags || [])}
                                personTags={personTags}
                                personLabel={musician.name || 'Unnamed'}
                                personMarks={markFavourites}
                                onRenameFavourite={renameFavourite}
                                inheritedShared={inheritedSharedFor(inst)}
                                hasConflict={!!conflict}
                conflictMessage={conflict ? formatConflictMessage(t, conflict) : undefined}
                index={musician.instruments.indexOf(inst)}
                total={musician.instruments.length}
                onChange={handleInstrumentChange}
                onMoveUp={() => moveInstrumentUp(inst.id)}
                onMoveDown={() => moveInstrumentDown(inst.id)}
                onRemove={() => {
                  if (musician) {
                    setMusician({
                      ...musician,
                      instruments: musician.instruments.filter(i => i.id !== inst.id),
                    });
                  }
                }}
              />
              );
            })}
          </div>
        </CollapsibleSection>

        {/* Backline Section */}
        <CollapsibleSection
          title={musician.name ? t('musicianEdit.backlineTitleFor', { name: musician.name }) : t('musicianEdit.backlineTitle')}
          headerActions={
            <Button variant="primary" size="sm" onClick={addBacklineItem}>
              {t('musicianEdit.addBackline')}
            </Button>
          }
        >
          {(musician.backline || []).length === 0 && (
            <p className="empty-state">{t('musicianEdit.noBackline')}</p>
          )}

          <BacklineEditor
            items={musician.backline || []}
            onChange={(items) => setMusician({ ...musician, backline: items })}
          />
          {/* VenueMicStandsTable RETIRÉ du bloc backline (2026-10-17, user) : le backline
              concerne les instruments — les pieds de micro (type, boom, venue-provided)
              vivent UNIQUEMENT dans la section Mic Stands du rider (PDF + page) et dans
              le tableau des pistes qui les définit. */}
<datalist id="diet-types-suggestions">
            {dietTypes.map((d) => <option key={d} value={d} />)}
          </datalist>
        </CollapsibleSection>
      </div>
    </div>
  );
}